import { Component, inject, Input } from '@angular/core';
import { BaseViewComponent } from '../base-view.component';
import { StateChangedModel } from '../../common/services/state.service';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { SecureDataService } from '../../common/services/securedata.sevice';
import { take } from 'rxjs';
import { LanguageStateService } from '../../common/services/language-state.service';
import { isSignatureRequired } from '../../common/utils/signature-type';

export interface OrderCheckListModel {
  Id: string;
  Seq: number;
  Header: string;
  Disclosure: string;
  DisclosureHtml: string;
  ProductType: string;

}

export interface OrderCheckListRequest {
  id: string,
  isChecked: boolean;
}

export interface OrderEventCheckListRequest {
  signature: string;
  timestamp: string;
  data: OrderCheckListRequest[];
  isSign: boolean;
}

enum SignatureType {
  none,
  full,
  initials
}

@Component({
    selector: 'app-order-checklist',
    templateUrl: './order-checklist.component.html',
    styleUrl: './order-checklist.component.scss',
    standalone: false
})
export class OrderCheckListComponent extends BaseViewComponent {
  signType = SignatureType.none;
  languageService: LanguageStateService = inject(LanguageStateService)

  protected override onSecureDataChanged(val: StateChangedModel): void {
    if (val && val.Data) {
      this.termsModel = val.Data;
      this.allOrderList = (this.termsModel?.terms as OrderCheckListModel[])?.sort((a, b) => a.Seq - b.Seq) ?? [];
      this.signType = this.termsModel.sigType;
      this.signatureRequired = isSignatureRequired(this.termsModel.sigType);
      this.isConsumerAgreementChecked = [];
      this.allOrderList.forEach(term => {
        this.isConsumerAgreementChecked.push({ id: term.Id, isChecked: false });
        this.orderCheckListForm.addControl(
          `item_${term.Id}`,
          new FormControl(false, Validators.requiredTrue)
        )

      });

      if (this.signatureRequired) {
        this.orderCheckListForm.addControl(
          `signType`,
          new FormControl(null, Validators.required)
        )
      }
    }
  }

  #secureDataService: SecureDataService = inject(SecureDataService)
  sanitizer = inject(DomSanitizer);
  orderCheckListForm: FormGroup = new FormGroup({});
  termsText: any;

  signatureRequired = true;
  isConsumerAgreementChecked: OrderCheckListRequest[] = [];
  isConsumerAgreementVisible = false;
  isRequiredConfirmVisible = false;

  externalLinkPopup = false;
  termsModel: any;
  allOrderList: OrderCheckListModel[] = [];
  sanitizedExternalLink: string = '';
  showErrors: boolean[] = [false];
  selectedItem = '';
  name = '';
  nameSelected = false;
  isSubmitted = false;
  showCancelDialog = false;


  @Input() control: AbstractControl | undefined;




  makeLinksClickable(text: string): SafeHtml {
    if (!text) return ''

    // First, handle existing anchor tags
    let modifiedHtml = this.modifyExistingAnchors(text);

    // Then, convert plain URLs to anchor tags
    const urlRegex = /(https?:\/\/[^\s<]+)(?![^<]*>|[^<>]*<\/a>)/g;
    modifiedHtml = modifiedHtml.replace(urlRegex, (match) => {
      return `<a href="${match}" class="external-link text-primary" target="_blank" rel="noopener noreferrer">${match}</a>`;
    });

    return this.sanitizer.bypassSecurityTrustHtml(modifiedHtml);
  }

  private modifyExistingAnchors(html: string): string {
    // Create a temporary container to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Find all anchor tags
    const anchors = tempDiv.querySelectorAll('a');

    anchors.forEach((anchor: HTMLAnchorElement) => {
      // Add classes
      anchor.classList.add('external-link', 'text-primary');

      // Add target="_blank" and security attributes
      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noopener noreferrer');
    });

    return tempDiv.innerHTML;
  }

  onConsumerAgreementChecked(termId: string, event: any) {
    if (event.checked) {
      //if terms & condition not selected
      const item = this.isConsumerAgreementChecked.find(term => term.id === termId);
      if (item !== undefined) {
        item.isChecked = false;
      }
      this.notificationService.showWarning('Please read the terms by clicking the link before accepting.')
      const control = this.orderCheckListForm.get(`item_${termId}`)
      if (control) {
        control.setValue(false)
      }
    }

  }

  onConsumerAgreementClick(term: OrderCheckListModel) {
    this.selectedItem = term.Id;
    if (term.DisclosureHtml != '') {
      this.termsText = this.makeLinksClickable(term.DisclosureHtml);
      this.isConsumerAgreementVisible = true;
      this.externalLinkPopup = false;
    }
    return false;

  }

  onExternalPopupLinkClick(checked: boolean) {
    if (!checked) {
      this.externalLinkPopup = checked;
      this.isConsumerAgreementVisible = !checked;
    } else {
      this.externalLinkPopup = false;
      window.open(this.sanitizedExternalLink, '_blank');
    }
  }

  onExternalPopupAction(data: unknown) {
    if (data && (data as { data: { success: boolean } }).data?.success) {
      this.onExternalPopupLinkClick(true);
    }
    else {
      this.onExternalPopupLinkClick(false);
    }
  }

  onRequiredPopupAction(data: unknown) {
    if (data && (data as { data: { success: boolean } }).data?.success) {
      this.onRequiredFieldSubmit();
    }

  }

  handleLinkClick(event: Event) {
    const target = event.target as HTMLElement;

    if (target.tagName === 'A') {
      event.preventDefault(); // Prevent default navigation
      this.externalLinkPopup = true;
      this.isConsumerAgreementVisible = false;
    }
  }


  private signedRequiredValidation() {
    if (this.name.length == 0) {
      this.isRequiredConfirmVisible = true;
      this.nameSelected = true;
      this.signType = SignatureType.none;
      return;
    }
    else {
      this.nameSelected = false;
      this.signType = SignatureType.initials;
    }
  }

  onFormSubmit() {

    this.isSubmitted = true;
    if (this.orderCheckListForm.valid) {

      const requestData: OrderCheckListRequest[] = [];
      this.isConsumerAgreementChecked.forEach((term) => {
        requestData.push({ id: term.id, isChecked: true });
      });


      const orderEventCheckListRequest: OrderEventCheckListRequest = {
        signature: this.orderCheckListForm.get('signType')?.value,
        timestamp: new Date().toISOString(),
        data: requestData,
        isSign: !this.signatureRequired ? false : true
      }
      this.#secureDataService.submitCustomerData(orderEventCheckListRequest, 'OrderCheckList')
        .subscribe({
          next: () => {
            this.navigateWaiting();
          },
          error: (err) => {
            this.notificationService.showError(err);
          }
        });

    } else {
      this.isRequiredConfirmVisible = true;
    }

  }

  onFormCancel() {
    this.showCancelDialog = true;

  }


  onCancelConfimation(data: unknown) {
    this.showCancelDialog = false;

    if (data && (data as { data: { success: boolean } }).data?.success) {
      this.isSubmitted = false;
      this.nameSelected = false;
      this.signType = SignatureType.none;
      this.name = '';
      this.orderCheckListForm.reset();

      this.#secureDataService.cancel('OrderCheckList').pipe(take(1)).subscribe(
        {
          next: () => {
            this.navigateWaiting(true);
          },
          error: (err) => this.onCancelError('OrderCheckList', err)
        })
    }

  }

  onNameChange() {
    this.nameSelected = false;
  }

  onPopupSubmit() {
    this.isConsumerAgreementVisible = false;
    this.externalLinkPopup = false;
    const item = this.isConsumerAgreementChecked.find(term => term.id === this.selectedItem);
    if (item !== undefined) {
      item.isChecked = true;
    }
    const control = this.orderCheckListForm.get(`item_${this.selectedItem}`)
    if (control) {
      control.setValue(true)
    }
  }

  isNameRequired() {
    this.nameSelected = !this.nameSelected;
  }

  onPopupCancel() {
    this.isConsumerAgreementVisible = false;
    this.externalLinkPopup = false;
    this.isConsumerAgreementChecked[this.selectedItem] = false;
  }





  onRequiredFieldSubmit() {
    this.orderCheckListForm.markAllAsTouched();

    this.nameSelected = this.name.length === 0;
    for (let i = 0; i < this.isConsumerAgreementChecked.length; i++) {
      if (!this.isConsumerAgreementChecked[i]) {
        this.showErrors[i] = true;
      }

    }
    this.isRequiredConfirmVisible = false;
  }

  onTitleUrlAction(event: Event) {
    this.handleLinkClick(event);
  }

  onCancel(data: unknown) {

    if (data && (data as { data: { success: boolean } }).data?.success) {
      this.onPopupSubmit();
    }
    else {
      this.onPopupCancel();
    }

  }

  formRequiredValidationShow() {

    if (this.isSubmitted) {

      return !this.signatureRequired ?
        !this.orderCheckListForm.valid : !(this.name.length > 0 && this.orderCheckListForm.valid);
    }


    return false;


  }


}


